import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FloatingShapes from '@/components/FloatingShapes';
import { teamMembers } from '@/mocks/team';
import TeamMemberCard, { type TeamMember } from './TeamMemberCard';

export default function AboutTeamSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="relative py-14 md:py-16 bg-background-100/60 overflow-hidden">
      <FloatingShapes count={14} color="secondary" speed={0.5} />
      <div className="absolute inset-0 pointer-events-none">
        <FloatingShapes count={8} color="accent" speed={0.5} />
      </div>

      <div className="absolute top-0 right-0 w-80 h-80 bg-accent-500/5 rounded-full blur-3xl animate-[morphPulse_8s_ease-in-out_infinite]" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-secondary-500/6 rounded-full blur-3xl animate-[morphFloat_7s_ease-in-out_infinite_1s]" />

      <div className="relative w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div
            className={`text-center mb-12 transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <span className="inline-block px-3 py-1 bg-accent-100/80 rounded-full text-[10px] font-mono font-medium text-accent-700 uppercase tracking-widest mb-4">
              {t('about.team.badge')}
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-semibold text-foreground-950 leading-tight tracking-tight max-w-3xl mx-auto">
              {t('about.team.title')}
            </h2>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl mx-auto mt-4">
              {t('about.team.paragraph')}
            </p>
            <p className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-accent-600 mt-5">
              <i className="ri-loop-right-line" />
              {t('about.team.flipHint')}
            </p>
          </div>

          {/* Team grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {teamMembers.map((member, i) => (
              <TeamMemberCard
                key={member.id}
                member={member as TeamMember}
                index={i}
                isVisible={isVisible}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}